import * as z from "zod";

// The calendar of a season (P8.16f, ADR-325): each domain answers the deadlines of its rows, grouped by kind, dates and
// brand with how many rows share them; worklist asks the domains in one call and merges them. `reference.season` has
// no dates: the span of a season is the span of its deadlines.

/** The kinds of deadline, by the domain that owns their rows. */
export const seasonDeadlineKindsOf = {
  catalog: ["attributes_due", "linesheet_inbound_window", "linesheet_outbound_window"],
  booking: ["campaign"],
  sales: ["sales_order_delivery_window"],
  procurement: ["po_confirmation_due", "po_delivery_window", "inbound_ready_for_pickup", "inbound_expected_delivery"],
} as const;
export type SeasonDeadlineDomain = keyof typeof seasonDeadlineKindsOf;
export type SeasonDeadlineKind<D extends SeasonDeadlineDomain = SeasonDeadlineDomain> =
  (typeof seasonDeadlineKindsOf)[D][number];

export const seasonDeadlineDomains = Object.keys(seasonDeadlineKindsOf) as [
  SeasonDeadlineDomain,
  ...SeasonDeadlineDomain[],
];

export const seasonDeadlineKinds = Object.values(seasonDeadlineKindsOf).flat() as [
  SeasonDeadlineKind,
  ...SeasonDeadlineKind[],
];

/** The domain whose rows a kind dates. */
export const domainOfDeadline = (kind: SeasonDeadlineKind): SeasonDeadlineDomain =>
  seasonDeadlineDomains.find((domain) =>
    (seasonDeadlineKindsOf[domain] as readonly string[]).includes(kind),
  ) as SeasonDeadlineDomain;

/**
 * The permission of the list a kind opens: the domain refuses a kind without it, and the app does not ask it. The
 * permissions are the lists' own (ADR-325 §3).
 */
export const seasonDeadlinePermissions = {
  attributes_due: "attributes.read",
  linesheet_inbound_window: "linesheet.read",
  linesheet_outbound_window: "linesheet.read",
  campaign: "booking.read",
  sales_order_delivery_window: "sales_order.read",
  po_confirmation_due: "purchase_order.read",
  po_delivery_window: "purchase_order.read",
  inbound_ready_for_pickup: "inbound.read",
  inbound_expected_delivery: "inbound.read",
} as const satisfies Record<SeasonDeadlineKind, string>;

/** The kinds among `kinds` whose list the permissions read. */
export const readableDeadlineKinds = <K extends SeasonDeadlineKind>(
  permissions: readonly string[],
  kinds: readonly K[],
): K[] => kinds.filter((kind) => permissions.includes(seasonDeadlinePermissions[kind]));

const named = z.object({ id: z.uuid(), name: z.string() });

/**
 * Rows of one kind sharing their dates and brand: a day (`to` null) or a window from `from` to `to`, both included.
 * `brand` null for a kind without one (the campaigns), `campaign` only for the campaigns.
 */
export const seasonDeadlineDto = z
  .object({
    kind: z.enum(seasonDeadlineKinds),
    from: z.iso.date(),
    to: z.iso.date().nullable(),
    brand: named.nullable(),
    campaign: named.nullable(),
    count: z.number().int().positive(),
  })
  .meta({ id: "SeasonDeadline" });
export type SeasonDeadline = z.output<typeof seasonDeadlineDto>;

/** A season by its code, the filter the lists the deadlines open take (`?season=SS27`). */
export const seasonCode = z.string().trim().min(1).max(20);

const kindsOf = <const K extends readonly [string, ...string[]]>(kinds: K) =>
  z
    .array(z.enum(kinds))
    .min(1)
    .max(kinds.length)
    .refine((asked) => new Set(asked).size === asked.length, "A kind asked twice");

/** What a domain is asked: the kinds of its own, in the season, of one brand or of every brand the Actor sees. */
export const seasonDeadlinesInputOf = <const K extends readonly [string, ...string[]]>(kinds: K) =>
  z.object({ season: seasonCode, kinds: kindsOf(kinds), brand: z.uuid().optional() });

/**
 * The answer of a domain for one kind, in the order asked: its deadlines, or the code that refused that kind alone (a
 * kind the Actor cannot read), as a count by filters answers (ADR-269).
 */
export type KindDeadlines<K extends string = SeasonDeadlineKind> =
  | { kind: K; deadlines: SeasonDeadline[] }
  | { kind: K; refused: string };

/** The kinds asked of worklist: in a query string comma-separated (`?kinds=campaign,po_delivery_window`). */
export const askedDeadlineKinds = <const K extends readonly [string, ...string[]]>(kinds: K) =>
  z
    .union([
      kindsOf(kinds),
      z
        .string()
        .max(kinds.join(",").length)
        .transform((asked) => asked.split(",")),
    ])
    .pipe(kindsOf(kinds));
